import React from "react";
import { connect } from "../../stores";
import HeroBanner from "./HeroBanner";
import FullPageBanner from "./FullPageBanner";
import TileBanner from "./TileBanner";
import ArticlesBanner from "./ArticlesBanner";
import {
    trackBannerImpression,
    trackArticleBannerImpression,
    trackBannerGroupImpression
} from "../../utils/BannerTrackingUtil";
import ClickHandler from "./ClickHandler";
import CarouselBanner from "./CarouselBanner";

import { FeatureSessionStore } from "../../uidispatcherapi/FeatureSessionStore";
import Features from "../../uidispatcherapi/Features";
import { EmptyBanner, BannerGroup, Banner, BannerStoreActions, BannerRequest } from "../../stores/BannerStore";
import { BannerType, PageType, TargetParams } from "../../utils/BannerContainer";
import { isEmptyBanner } from "../../utils/BannerUtil";

// we need the banner type explicitly, because we want to render placeholders if the banner
// is currently loading and therefore null.
type PropType = {
    bannerType: BannerType;
    pageType: PageType;
    bannerGroup: BannerGroup;
    targetParams: TargetParams;
    onBannerLoaded: () => void;
    customerStoreId: string;
};

function evaluateBannerHash(banners: BannerGroup) {
    return banners && banners.map(banner => banner.placementId).join("-");
}
function isBannerGroupLoaded(bannerGroup: BannerGroup) {
    return bannerGroup && bannerGroup.length !== 0 && !isEmptyBanner(bannerGroup[0]);
}
function asBannerRequest(props: PropType): BannerRequest {
    return { pageType: props.pageType, bannerType: props.bannerType, targetParams: props.targetParams };
}

class BannerComponent extends React.Component<PropType> {
    bannerHash: string;
    constructor(props: PropType) {
        super(props);
        this.bannerHash = evaluateBannerHash(props.bannerGroup);
    }

    componentDidMount() {
        if (isBannerGroupLoaded(this.props.bannerGroup)) {
            this.trackImpression(this.props);
            this.callBack(this.props);
        } else {
            BannerStoreActions.LoadBannerAction(asBannerRequest(this.props));
        }
    }

    componentWillUnmount() {
        BannerStoreActions.DisposeBannerAction(asBannerRequest(this.props));
    }

    callBack(props: PropType) {
        if (props.onBannerLoaded) {
            props.onBannerLoaded();
        }
    }

    getBundleId(article: { [x: string]: any }): string {
        return article.bettyBundleId.bettyBundleId;
    }

    trackImpression(props: PropType) {
        const bannerTargetParams = props.targetParams || {};
        if (props.bannerGroup && props.bannerGroup.length > 0) {
            if (props.bannerGroup.length > 1) {
                trackBannerGroupImpression(props.bannerGroup, props.pageType, bannerTargetParams);
                bannerTargetParams.isCarouselMember = true;
            }
            const banner = props.bannerGroup[0];
            if (banner.type === "articlesTilesTeaser") {
                const bettyBundleIds = (banner.articleData || [])
                    .filter(article => article !== null)
                    .map(article => this.getBundleId(article));
                trackArticleBannerImpression(banner, props.pageType, bettyBundleIds, bannerTargetParams);
            } else {
                trackBannerImpression(banner, props.pageType, bannerTargetParams);
            }
        }
    }

    componentWillReceiveProps(newProps: PropType) {
        const oldBannerHash = this.bannerHash;
        const newBannerHash = evaluateBannerHash(newProps.bannerGroup);

        // only track and call back if banner changed
        if (oldBannerHash !== newBannerHash) {
            this.trackImpression(newProps);
            this.callBack(newProps);
            this.bannerHash = newBannerHash;
        }
    }

    withDefaultClickHandler(banner: Banner | EmptyBanner, renderedBanner: JSX.Element) {
        return (
            <ClickHandler banner={banner} targetParams={this.props.targetParams} pageType={this.props.pageType}>
                {renderedBanner}
            </ClickHandler>
        );
    }

    withTileBannerClickHandler(banner: Banner | EmptyBanner, renderedBanner: JSX.Element) {
        return <div className="mfcss_teaser-tile-Banner">{this.withDefaultClickHandler(banner, renderedBanner)}</div>;
    }

    renderBanner(banner: Banner | EmptyBanner) {
        switch (this.props.bannerType) {
            case "heroBanner":
                return this.withDefaultClickHandler(banner, <HeroBanner banner={banner} />);
            case "fullPageBanner":
                return this.withDefaultClickHandler(banner, <FullPageBanner banner={banner} />);
            case "tileBanner":
                return (
                    !isEmptyBanner(banner) && this.withTileBannerClickHandler(banner, <TileBanner banner={banner} />)
                );
            case "articlesTilesTeaser":
                return this.withDefaultClickHandler(
                    banner,
                    <ArticlesBanner
                        banner={banner}
                        targetParams={this.props.targetParams}
                        pageType={this.props.pageType}
                    />
                );
            default:
                return null;
        }
    }

    renderBannerGroup(banners: BannerGroup) {
        return (
            <CarouselBanner banners={banners} targetParams={this.props.targetParams} pageType={this.props.pageType} />
        );
    }

    render() {
        //a baner wasn't provided, still delegating to specific banner implementaiton strategy
        if (!this.props.bannerGroup || this.props.bannerGroup.length === 0) {
            return this.renderBanner({ type: this.props.bannerType, isEmpty: true });
        }

        if (this.props.bannerGroup.length > 1) {
            const isCarouselFeatureOn = FeatureSessionStore.getMembershipFor(Features.CIA_BANNER_CAROUSEL) === "B";
            if (isCarouselFeatureOn) {
                return this.renderBannerGroup(this.props.bannerGroup);
            } else {
                return this.renderBanner(this.props.bannerGroup[0]);
            }
        } else {
            //fallback: we always render a single banner
            const banner = this.props.bannerGroup[0];
            return this.renderBanner(banner);
        }
    }
}

const wrapped = connect(
    {
        stores: {
            BannerStore: (store: any, props: PropType) => ({
                bannerGroup: store.getBannerGroup(asBannerRequest(props))
            }),
            SessionStore: (store: any) => ({ customerStoreId: store.getCustomerStore() })
        }
    },
    BannerComponent
);

export default wrapped;
